import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, adminAuth, doctorsApi } from '../services/api';
import {
  Users, Video, MessageSquare, BarChart3, Plus, Settings, Play,
  Server, Activity, X, RefreshCw, Image as ImageIcon, Stethoscope, Upload,
  LogOut, AlertTriangle, MicOff, Volume2, CheckCircle2,
} from 'lucide-react';

export default function AdminDashboard() {
  const [tab, setTab] = useState('overview'); // overview | clients | nodes | rooms
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [nodesPayload, setNodesPayload] = useState(null);
  const [activeRooms, setActiveRooms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    // allSettled so that a single failing endpoint (ex: Cloudflare 522 on
    // /templates) doesn't wipe out the whole dashboard — each slot falls back
    // to its empty default independently.
    const [s, c, n, r, t] = await Promise.allSettled([
      adminApi.getStats(),
      adminApi.listClients(),
      adminApi.listNodes(),
      adminApi.listActiveRooms(),
      adminApi.listTemplates(),
    ]);
    const val = (p, fallback) => p.status === 'fulfilled' ? p.value : fallback;
    setStats(val(s, null));
    const cv = val(c, []); setClients(Array.isArray(cv) ? cv : []);
    setNodesPayload(val(n, null));
    const rv = val(r, []); setActiveRooms(Array.isArray(rv) ? rv : []);
    const tv = val(t, []); setTemplates(Array.isArray(tv) ? tv : []);
    [s, c, n, r, t].forEach((p, i) => {
      if (p.status === 'rejected') console.error('admin refresh slot', i, p.reason);
    });
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800 px-6 py-4 sticky top-0 bg-slate-950/80 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Video size={18} />
            </div>
            <h1 className="text-xl font-bold">Help Video Call</h1>
            <span className="text-xs bg-indigo-600/20 text-indigo-400 px-2 py-0.5 rounded-full">Admin</span>
          </div>
          <div className="flex gap-2">
            <button onClick={refresh} className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm">
              <RefreshCw size={14} /> Atualizar
            </button>
            <Link to="/playground" className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm">
              <Play size={14} /> API Playground
            </Link>
            <button
              onClick={() => adminAuth.logout()}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-red-900/60 rounded-lg text-sm"
              title="Encerrar sessao"
            >
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Tab nav */}
        <nav className="flex gap-1 mb-6 bg-slate-900 rounded-xl p-1 w-fit flex-wrap">
          <TabBtn active={tab === 'overview'} onClick={() => setTab('overview')} icon={<BarChart3 size={14} />}>Visao geral</TabBtn>
          <TabBtn active={tab === 'clients'} onClick={() => setTab('clients')} icon={<Users size={14} />}>Clientes</TabBtn>
          <TabBtn active={tab === 'nodes'} onClick={() => setTab('nodes')} icon={<Server size={14} />}>Servidores</TabBtn>
          <TabBtn active={tab === 'rooms'} onClick={() => setTab('rooms')} icon={<Activity size={14} />}>Salas Ativas</TabBtn>
          <TabBtn active={tab === 'templates'} onClick={() => setTab('templates')} icon={<ImageIcon size={14} />}>Templates</TabBtn>
          <TabBtn active={tab === 'doctors'} onClick={() => setTab('doctors')} icon={<Stethoscope size={14} />}>Medicos</TabBtn>
          <TabBtn active={tab === 'diagnostics'} onClick={() => setTab('diagnostics')} icon={<AlertTriangle size={14} />}>Diagnostico</TabBtn>
        </nav>

        {tab === 'overview' && <OverviewTab stats={stats} nodesPayload={nodesPayload} activeRooms={activeRooms} />}
        {tab === 'clients' && <ClientsTab clients={clients} />}
        {tab === 'nodes' && <NodesTab payload={nodesPayload} onChange={refresh} />}
        {tab === 'rooms' && <RoomsTab rooms={activeRooms} onChange={refresh} />}
        {tab === 'templates' && <TemplatesTab templates={templates} clients={clients} onChange={refresh} />}
        {tab === 'doctors' && <DoctorsTab clients={clients} templates={templates} />}
        {tab === 'diagnostics' && <DiagnosticsTab />}
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition ${
        active ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-800'
      }`}
    >
      {icon}{children}
    </button>
  );
}

function StatCard({ label, value, icon: Icon, color, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`bg-slate-900 border border-slate-800 rounded-xl p-5 ${clickable ? 'cursor-pointer hover:border-slate-600 transition-colors' : ''}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-slate-400 text-sm">{label}</span>
        <Icon size={18} className={`text-${color}-400`} />
      </div>
      <span className="text-3xl font-bold">{value}</span>
    </div>
  );
}

function OverviewTab({ stats, nodesPayload, activeRooms }) {
  const nodeCount = (nodesPayload?.nodes?.length || 0);
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Clientes" value={stats?.totalClients || 0} icon={Users} color="indigo" />
        <StatCard label="Servidores" value={nodeCount} icon={Server} color="sky" />
        <StatCard label="Salas Ativas" value={stats?.activeRooms || 0} icon={Video} color="emerald" />
        <StatCard label="Total Salas" value={stats?.totalRooms || 0} icon={BarChart3} color="blue" />
        <StatCard label="Mensagens" value={stats?.totalMessages || 0} icon={MessageSquare} color="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Server size={16} /> Uso dos servidores</h3>
          <NodeBars payload={nodesPayload} />
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Activity size={16} /> Salas ativas agora</h3>
          {activeRooms.length === 0 ? (
            <p className="text-slate-500 text-sm">Nenhuma sala ativa.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {activeRooms.slice(0, 8).map(r => (
                <li key={r.roomId} className="flex items-center justify-between py-1">
                  <span className="font-mono text-xs text-slate-400">{r.roomId}</span>
                  <span>{r.dbParticipants} participantes</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

function NodeBars({ payload }) {
  if (!payload) return null;
  const items = [
    { name: 'default (config)', active: payload.defaultNode.activeRooms, max: 100 },
    ...(payload.nodes || []).map(n => ({ name: n.name, active: n.activeRooms, max: n.maxRooms, region: n.region, isActive: n.isActive })),
  ];
  return (
    <ul className="space-y-3">
      {items.map((n, i) => {
        const pct = Math.min(100, Math.round((n.active / n.max) * 100));
        const color = pct > 85 ? 'bg-rose-500' : pct > 60 ? 'bg-amber-500' : 'bg-emerald-500';
        return (
          <li key={i}>
            <div className="flex justify-between text-xs mb-1">
              <span>{n.name} {n.region && <span className="text-slate-500">({n.region})</span>}</span>
              <span className="text-slate-400">{n.active}/{n.max}</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ClientsTab({ clients }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl">
      <div className="flex items-center justify-between p-5 border-b border-slate-800">
        <h2 className="text-lg font-semibold">Clientes</h2>
        <Link to="/admin/clients/new" className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm">
          <Plus size={14} /> Novo Cliente
        </Link>
      </div>
      {clients.length === 0 ? (
        <div className="p-12 text-center text-slate-500">
          <Users size={48} className="mx-auto mb-4 opacity-30" />
          <p>Nenhum cliente cadastrado</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-800">
          {clients.map((client) => (
            <Link key={client.id} to={`/admin/clients/${client.id}`}
              className="flex items-center justify-between p-4 hover:bg-slate-800/50">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-lg font-bold text-indigo-400">
                  {client.nome?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-medium">{client.nome}</p>
                  <p className="text-xs text-slate-500 font-mono">{client.apiKey}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm">{client.salasAtivas || 0} salas ativas</p>
                  <p className="text-xs text-slate-500">{client.dominio || 'sem dominio'}</p>
                </div>
                <span className={`w-2.5 h-2.5 rounded-full ${client.ativo ? 'bg-emerald-500' : 'bg-slate-600'}`}></span>
                <Settings size={16} className="text-slate-500" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function NodesTab({ payload, onChange }) {
  const [showForm, setShowForm] = useState(false);
  return (
    <div>
      {payload?.defaultNode && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Server size={16} className="text-slate-400" />
            <span className="font-semibold">Servidor padrao (appsettings.json)</span>
            <span className="text-xs text-slate-500">{payload.defaultNode.note}</span>
          </div>
          <p className="text-sm font-mono text-slate-400">{payload.defaultNode.apiUrl}</p>
          <p className="text-sm mt-2">{payload.defaultNode.activeRooms} salas ativas / {payload.defaultNode.totalRooms} total</p>
        </div>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <h2 className="text-lg font-semibold">Servidores LiveKit adicionais</h2>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm">
            <Plus size={14} /> Adicionar Servidor
          </button>
        </div>
        {(payload?.nodes?.length || 0) === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Server size={48} className="mx-auto mb-4 opacity-30" />
            <p>Nenhum servidor adicional cadastrado.</p>
            <p className="text-xs mt-2">Quando o servidor padrao passar de 80% de uso, adicione um aqui pra escalar.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {payload.nodes.map(node => <NodeRow key={node.id} node={node} onChange={onChange} />)}
          </div>
        )}
      </div>

      {showForm && <NodeFormModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); onChange(); }} />}
    </div>
  );
}

function NodeRow({ node, onChange }) {
  const pct = Math.min(100, Math.round((node.activeRooms / node.maxRooms) * 100));
  const color = pct > 85 ? 'bg-rose-500' : pct > 60 ? 'bg-amber-500' : 'bg-emerald-500';
  const handleDelete = async () => {
    if (!window.confirm(`Remover servidor ${node.name}?`)) return;
    const res = await adminApi.deleteNode(node.id);
    if (res?.error) alert(res.error); else onChange();
  };
  return (
    <div className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-medium flex items-center gap-2">
            {node.name}
            <span className={`w-2 h-2 rounded-full ${node.isActive ? 'bg-emerald-500' : 'bg-slate-600'}`}></span>
            {node.region && <span className="text-xs text-slate-500">({node.region})</span>}
          </p>
          <p className="text-xs text-slate-500 font-mono">{node.apiUrl}</p>
          <p className="text-xs text-slate-500 font-mono">{node.wsUrl}</p>
        </div>
        <button onClick={handleDelete} className="text-rose-500 hover:text-rose-400 p-1" title="Remover">
          <X size={16} />
        </button>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <span>{node.activeRooms}/{node.maxRooms} salas</span>
        <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-slate-500">{pct}%</span>
      </div>
    </div>
  );
}

function NodeFormModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', apiUrl: '', wsUrl: '', apiKey: '', apiSecret: '',
    region: '', maxRooms: 100, isActive: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const res = await adminApi.createNode(form);
      if (res?.error) setError(res.error);
      else onSaved();
    } catch (err) { setError('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Adicionar Servidor LiveKit</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="space-y-3">
          <Field label="Nome" value={form.name} onChange={v => update('name', v)} placeholder="livekit-02" />
          <Field label="Regiao" value={form.region} onChange={v => update('region', v)} placeholder="BR-Sul" />
          <Field label="Api URL (Twirp - servidor->LiveKit)" value={form.apiUrl} onChange={v => update('apiUrl', v)} placeholder="http://livekit-02:7880" />
          <Field label="Ws URL (cliente->LiveKit)" value={form.wsUrl} onChange={v => update('wsUrl', v)} placeholder="wss://livekit-02.dominio.com.br" />
          <Field label="Api Key" value={form.apiKey} onChange={v => update('apiKey', v)} />
          <Field label="Api Secret" value={form.apiSecret} onChange={v => update('apiSecret', v)} type="password" />
          <Field label="Capacidade (salas simultaneas)" value={form.maxRooms} onChange={v => update('maxRooms', Number(v) || 100)} type="number" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isActive} onChange={e => update('isActive', e.target.checked)} />
            Ativo (o sistema vai alocar salas pra ele)
          </label>
        </div>

        {error && <p className="text-rose-400 text-sm mt-3">{error}</p>}

        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-500 rounded-lg py-2.5 font-medium disabled:opacity-50">
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
          <button onClick={onClose} className="px-4 bg-slate-800 hover:bg-slate-700 rounded-lg">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-slate-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );
}

function RoomsTab({ rooms, onChange }) {
  const [selected, setSelected] = useState(null);

  const handleForceClose = async (e, roomId) => {
    e.stopPropagation();
    if (!window.confirm(`Encerrar sala ${roomId}?\n\nIsso desconecta qualquer participante e marca como encerrada.`)) return;
    const res = await adminApi.forceCloseRoom(roomId);
    if (res?.error) alert(res.error);
    if (selected === roomId) setSelected(null);
    onChange?.();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-800 flex justify-between items-center">
          <h2 className="font-semibold">Salas ativas ({rooms.length})</h2>
          <span className="text-xs text-slate-500">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1"></span> = ao vivo agora
          </span>
        </div>
        {rooms.length === 0 ? (
          <div className="p-12 text-center text-slate-500">Nenhuma sala ativa.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-950/50 text-xs text-slate-400 uppercase">
              <tr>
                <th className="text-left p-3">Room</th>
                <th className="text-left p-3">Cliente</th>
                <th className="text-left p-3">Servidor</th>
                <th className="text-left p-3">Ao vivo agora</th>
                <th className="text-right p-3">Iniciou</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rooms.map(r => {
                const live = r.live || [];
                const ghost = live.length === 0 && r.dbParticipants > 0;
                return (
                  <tr
                    key={r.roomId}
                    className={`border-t border-slate-800 hover:bg-slate-800/50 cursor-pointer ${selected === r.roomId ? 'bg-slate-800/40' : ''}`}
                    onClick={() => setSelected(r.roomId)}
                  >
                    <td className="p-3 font-mono text-xs">{r.roomId}</td>
                    <td className="p-3">{r.client?.nome || '-'}</td>
                    <td className="p-3 text-xs text-slate-400">{r.nodeName}</td>
                    <td className="p-3">
                      {live.length === 0 ? (
                        <span className="text-slate-500 text-xs italic">
                          {ghost ? <span className="text-amber-400">vazia (ghost?)</span> : 'vazia'}
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {live.map(p => (
                            <ParticipantChip key={p.identity} name={p.name} role={p.role} />
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right text-xs text-slate-500">
                      {r.startedAt ? new Date(r.startedAt).toLocaleTimeString('pt-BR') : '-'}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={(e) => handleForceClose(e, r.roomId)}
                        className="text-xs text-rose-400 hover:text-rose-300 px-2 py-1 rounded hover:bg-rose-500/10"
                        title="Encerrar sala"
                      >
                        Encerrar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="lg:col-span-1">
        {selected ? (
          <ParticipantsPanel roomId={selected} onClose={() => setSelected(null)} />
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center text-slate-500 text-sm">
            Selecione uma sala pra ver os participantes
          </div>
        )}
      </div>
    </div>
  );
}

function ParticipantChip({ name, role }) {
  const isHost = role === 'host';
  const display = name || (isHost ? 'Anfitriao' : 'Participante');
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${
        isHost
          ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30'
          : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
      }`}
      title={`${isHost ? 'Medico (host)' : 'Paciente'}: ${display}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
      <span className="font-medium">{isHost ? '⚕' : '👤'}</span>
      <span className="max-w-[12rem] truncate">{display}</span>
    </span>
  );
}

function ParticipantsPanel({ roomId, onClose }) {
  const [data, setData] = useState(null);
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [p, n] = await Promise.allSettled([
          adminApi.roomParticipants(roomId),
          adminApi.roomNetwork(roomId, 10, 50),
        ]);
        if (cancelled) return;
        if (p.status === 'fulfilled') setData(p.value);
        if (n.status === 'fulfilled') setNetwork(n.value);
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    const interval = setInterval(load, 5000); // poll a cada 5s
    return () => { cancelled = true; clearInterval(interval); };
  }, [roomId]);

  // Agrupa amostras de rede por participante: pega a mais recente de cada
  // identity pra mostrar "estado atual" sem poluir com historico.
  const latestSampleByIdentity = (() => {
    const map = new Map();
    for (const s of (network?.samples || [])) {
      if (!s.participantIdentity) continue;
      if (!map.has(s.participantIdentity)) map.set(s.participantIdentity, s);
    }
    return map;
  })();

  // Ultimo evento quality_degraded por identity (decai depois de 60s sem novo
  // evento - se o LiveKit nao reportou mais nada e porque voltou ao normal).
  const latestQualityByIdentity = (() => {
    const map = new Map();
    const now = Date.now();
    for (const ev of (network?.events || [])) {
      if (ev.eventType !== 'quality_degraded') continue;
      if (!ev.participantIdentity || map.has(ev.participantIdentity)) continue;
      const age = now - new Date(ev.createdAt).getTime();
      if (age > 60_000) continue;
      map.set(ev.participantIdentity, ev);
    }
    return map;
  })();

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <div>
          <p className="font-semibold text-sm">{data?.room?.displayName || 'Sala'}</p>
          <p className="text-xs text-slate-500 font-mono">{roomId}</p>
        </div>
        <button onClick={onClose}><X size={16} /></button>
      </div>

      {loading && !data ? (
        <div className="p-6 text-center text-slate-500 text-sm">Carregando...</div>
      ) : (
        <>
          <div className="p-4 text-xs text-slate-500 flex items-center gap-2">
            <span>Servidor:</span>
            <span className="font-mono text-slate-300">{data?.room?.nodeName}</span>
          </div>

          <div className="p-4 border-t border-slate-800">
            <p className="text-xs uppercase text-slate-500 mb-2">Conectados agora</p>
            {(data?.live?.length || 0) === 0 ? (
              <p className="text-sm text-slate-500">Ninguem conectado.</p>
            ) : (
              <ul className="space-y-3">
                {data.live.map(p => (
                  <LiveParticipantRow
                    key={p.identity}
                    participant={p}
                    sample={latestSampleByIdentity.get(p.identity)}
                    qualityEvent={latestQualityByIdentity.get(p.identity)}
                  />
                ))}
              </ul>
            )}
          </div>

          {(network?.events?.length || 0) > 0 && (
            <div className="p-4 border-t border-slate-800">
              <p className="text-xs uppercase text-slate-500 mb-2">Eventos (10min)</p>
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {network.events.slice(0, 20).map(ev => (
                  <li key={ev.id} className={`text-xs px-2 py-1 rounded ${
                    ev.severity === 'error' ? 'bg-rose-950/30 text-rose-300'
                    : ev.severity === 'warn' ? 'bg-amber-950/20 text-amber-200'
                    : 'text-slate-400'
                  }`}>
                    <span className="font-mono text-slate-500 mr-2">
                      {new Date(ev.createdAt).toLocaleTimeString('pt-BR')}
                    </span>
                    <span className="font-mono text-slate-300 mr-2">{ev.eventType}</span>
                    {ev.displayName && <span className="text-slate-500 mr-2">· {ev.displayName}</span>}
                    <span>{ev.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="p-4 border-t border-slate-800">
            <p className="text-xs uppercase text-slate-500 mb-2">Historico ({data?.history?.length || 0})</p>
            <ul className="space-y-2 max-h-60 overflow-y-auto">
              {(data?.history || []).map((p, i) => (
                <li key={i} className="text-xs">
                  <div className="flex justify-between">
                    <span className="font-medium text-slate-300">{p.displayName}</span>
                    <span className={p.status === 'active' ? 'text-emerald-500' : 'text-slate-500'}>{p.status}</span>
                  </div>
                  <span className="text-slate-500">
                    {p.role} · entrou {new Date(p.joinedAt).toLocaleTimeString('pt-BR')}
                    {p.leftAt && ` · saiu ${new Date(p.leftAt).toLocaleTimeString('pt-BR')}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Linha individual da lista "Conectados agora". Mostra nome, papel, badge de
 * qualidade (vindo do LiveKit), badges de microfone e camera publicados
 * (se participante nao publicou = provavelmente nao deu permissao) e a
 * metrica mais recente de packet loss/bitrate da RTCStats do navegador.
 */
function LiveParticipantRow({ participant, sample, qualityEvent }) {
  const p = participant;
  const tracks = p.tracks || [];
  const micTrack = tracks.find(t => t.source === 'microphone');
  const camTrack = tracks.find(t => t.source === 'camera');

  const parsed = parseSampleMetadata(sample?.metadata);
  const metrics = parsed.metrics;
  const context = parsed.context;
  const inboundAudio = metrics.find(m => m.direction === 'inbound' && m.kind === 'audio');
  const inboundVideo = metrics.find(m => m.direction === 'inbound' && m.kind === 'video');
  const outboundAudio = metrics.find(m => m.direction === 'outbound' && m.kind === 'audio');
  const outboundVideo = metrics.find(m => m.direction === 'outbound' && m.kind === 'video');

  // Qualidade derivada: worst-case entre qualityEvent do LiveKit (Poor/Lost) e
  // o pior loss % das amostras recentes. Sem evento recente = sem badge.
  const qualityBadge = deriveQualityBadge(qualityEvent, metrics);

  return (
    <li className="text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
        <span className="font-medium">{p.name || p.identity}</span>
        {qualityBadge && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${qualityBadge.className}`}>
            {qualityBadge.label}
          </span>
        )}
        <TrackBadge kind="mic" track={micTrack} />
        <TrackBadge kind="cam" track={camTrack} />
      </div>
      <p className="text-[11px] text-slate-500 ml-4 font-mono">{p.identity}</p>

      {context && <NetworkContextLine context={context} />}

      {(inboundAudio || inboundVideo || outboundAudio || outboundVideo) && (
        <div className="ml-4 mt-2 space-y-1 text-[11px]">
          <MetricLine label="Recebendo audio"  m={inboundAudio} />
          <MetricLine label="Recebendo video"  m={inboundVideo} />
          <MetricLine label="Enviando audio"   m={outboundAudio} />
          <MetricLine label="Enviando video"   m={outboundVideo} />
        </div>
      )}
    </li>
  );
}

/**
 * Linha de contexto com info "da rede" (nao das midias). RTT, banda estimada,
 * tipo de conexao. Cada pedaco so aparece se tem valor - evita render de
 * coisas com "--" quando o navegador nao expoe (iOS Safari, principalmente).
 */
function NetworkContextLine({ context }) {
  const pieces = [];
  const rtt = context.subscriberRttMs ?? context.publisherRttMs;
  if (typeof rtt === 'number') {
    const cls = rtt > 300 ? 'text-rose-300' : rtt > 150 ? 'text-amber-300' : 'text-slate-300';
    pieces.push(<span key="rtt" className={`font-mono ${cls}`}>rtt {rtt}ms</span>);
  }
  const avail = context.availableOutgoingKbps;
  if (typeof avail === 'number' && avail > 0) {
    pieces.push(<span key="out" className="font-mono text-slate-400">envio ≤ {avail} kbps</span>);
  }
  const availIn = context.availableIncomingKbps;
  if (typeof availIn === 'number' && availIn > 0) {
    pieces.push(<span key="in" className="font-mono text-slate-400">recebe ≤ {availIn} kbps</span>);
  }
  const n = context.navigator;
  if (n) {
    // navigator.connection tem 2 campos com semantica DIFERENTE:
    //   - type: 'wifi' / 'cellular' / 'ethernet' - tipo REAL da conexao
    //     (raramente preenchido por privacidade, so Chrome Android dispara)
    //   - effectiveType: '4g' / '3g' / '2g' / 'slow-2g' - classe de
    //     QUALIDADE estimada por RTT+downlink. NUNCA retorna 'wifi':
    //     wifi bom = '4g' nessa metrica, wifi ruim = '3g'/'2g'.
    //
    // Antes mostravamos so '4g' e a label confundia o admin que via uma
    // chamada em wifi rotulada como 4g e ficava sem entender. Agora:
    //   - Se 'type' real esta disponivel -> mostra ele primeiro (wifi/cellular/etc)
    //   - Sempre mostra effectiveType com prefixo 'q' pra deixar claro que
    //     e qualidade estimada, nao tipo de rede
    if (n.type) {
      pieces.push(<span key="conn" className="font-mono text-slate-300">{n.type}</span>);
    }
    if (n.effectiveType) {
      const cls = n.effectiveType === '4g' ? 'text-emerald-300'
                : n.effectiveType === '3g' ? 'text-amber-300'
                : 'text-rose-300';
      pieces.push(
        <span
          key="qual"
          className={`font-mono ${cls}`}
          title="Qualidade estimada pelo browser (RTT+downlink). Nao e o tipo da rede - wifi com sinal bom aparece como 'q4g'."
        >q{n.effectiveType}</span>
      );
    }
    if (typeof n.downlinkMbps === 'number' && n.downlinkMbps > 0) {
      pieces.push(<span key="dl" className="font-mono text-slate-400">{n.downlinkMbps} Mbps</span>);
    }
    if (n.saveData) {
      pieces.push(<span key="sd" className="font-mono text-amber-300">economia de dados</span>);
    }
  }
  if (pieces.length === 0) return null;
  return (
    <div className="ml-4 mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500 items-center">
      <span className="uppercase tracking-wide text-[9px]">rede</span>
      {pieces}
    </div>
  );
}

function TrackBadge({ kind, track }) {
  // kind = 'mic' | 'cam'
  const label = kind === 'mic' ? 'Mic' : 'Cam';
  if (!track) {
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-500" title={`${label} nao publicado`}>
        {label} off
      </span>
    );
  }
  if (track.muted) {
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-600/20 text-amber-300" title={`${label} mudo`}>
        {label} mudo
      </span>
    );
  }
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-300" title={`${label} ativo`}>
      {label} on
    </span>
  );
}

function MetricLine({ label, m }) {
  if (!m) return null;
  const lossPct = m.packetsLostPct;
  const lossClass = typeof lossPct === 'number'
    ? (lossPct >= 10 ? 'text-rose-300' : lossPct >= 2 ? 'text-amber-300' : 'text-emerald-300')
    : 'text-slate-400';
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-slate-500 w-28">{label}</span>
      {typeof m.bitrateKbps === 'number' && (
        <span className="font-mono text-slate-300">{m.bitrateKbps} kbps</span>
      )}
      {typeof lossPct === 'number' && (
        <span className={`font-mono ${lossClass}`}>loss {lossPct}%</span>
      )}
      {typeof m.jitterMs === 'number' && m.jitterMs > 0 && (
        <span className="font-mono text-slate-500">jitter {m.jitterMs}ms</span>
      )}
      {typeof m.framesPerSecond === 'number' && (
        <span className="font-mono text-slate-500">{m.framesPerSecond}fps</span>
      )}
    </div>
  );
}

function parseSampleMetadata(rawMetadata) {
  if (!rawMetadata) return { metrics: [], context: null };
  try {
    const obj = JSON.parse(rawMetadata);
    return {
      metrics: Array.isArray(obj?.metrics) ? obj.metrics : [],
      context: obj?.context || null,
    };
  } catch { return { metrics: [], context: null }; }
}

// Combina sinal qualitativo do LiveKit (quality_degraded) com o pior loss %
// das amostras RTCStats pra escolher um unico badge de qualidade. Retorna
// null quando nao ha evidencia de problema - ai a UI nao mostra badge.
function deriveQualityBadge(qualityEvent, metrics) {
  let level = null; // 'poor' | 'lost' | null
  if (qualityEvent) {
    try {
      const md = JSON.parse(qualityEvent.metadata || '{}');
      if (md.quality === 'lost') level = 'lost';
      else if (md.quality === 'poor') level = 'poor';
    } catch { /* metadata sem quality, ignora */ }
  }
  const worstLoss = metrics
    .filter(m => m.direction === 'inbound' && typeof m.packetsLostPct === 'number')
    .reduce((acc, m) => Math.max(acc, m.packetsLostPct), 0);
  if (level === 'lost') {
    return { label: 'Perdida', className: 'bg-slate-600/30 text-slate-300' };
  }
  if (level === 'poor' || worstLoss >= 10) {
    return { label: 'Ruim', className: 'bg-rose-600/20 text-rose-300' };
  }
  if (worstLoss >= 2) {
    return { label: 'Instavel', className: 'bg-amber-600/20 text-amber-300' };
  }
  return null;
}

// ====================================================================
// TEMPLATES TAB
// ====================================================================

function TemplatesTab({ templates, clients, onChange }) {
  const [showForm, setShowForm] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Templates de fundo</h2>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm"
          disabled={clients.length === 0}
          title={clients.length === 0 ? 'Cadastre um cliente primeiro' : ''}
        >
          <Upload size={14} /> Novo Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center text-slate-500">
          <ImageIcon size={48} className="mx-auto mb-3 opacity-30" />
          <p>Nenhum template cadastrado.</p>
          <p className="text-xs mt-2">Suba um PNG (1280x720 funciona melhor) e defina onde o nome e CRM aparecem.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map(t => (
            <TemplateCard key={t.id} template={t} clients={clients} onChange={onChange} />
          ))}
        </div>
      )}

      {showForm && (
        <TemplateFormModal
          clients={clients}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); onChange(); }}
        />
      )}
    </div>
  );
}

function TemplateCard({ template, clients, onChange }) {
  const client = clients.find(c => c.id === template.apiClientId);
  const handleDelete = async () => {
    if (!window.confirm(`Remover template "${template.name}"?`)) return;
    const res = await adminApi.deleteTemplate(template.id);
    if (res?.error) alert(res.error); else onChange();
  };
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
      <div className="aspect-video bg-slate-800 flex items-center justify-center text-slate-600 text-xs relative overflow-hidden">
        {template.imageUrl ? (
          <img
            src={template.imageUrl}
            alt={template.name}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <ImageIcon size={48} />
        )}
        <span className="absolute bottom-2 right-2 text-[10px] font-mono text-slate-300 bg-black/60 px-1.5 py-0.5 rounded truncate max-w-[60%]">
          {template.templatePath?.split(/[\\/]/).pop()}
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium truncate flex items-center gap-2">
              {template.name}
              {template.isDefault && <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">DEFAULT</span>}
            </p>
            <p className="text-xs text-slate-500 truncate">{client?.nome || `Cliente #${template.apiClientId}`}</p>
          </div>
          <button onClick={handleDelete} className="text-rose-500 hover:text-rose-400 p-1" title="Remover">
            <X size={16} />
          </button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-400">
          <div>Nome: <span className="text-slate-300">x={template.nameX} y={template.nameY}</span></div>
          <div>CRM: <span className="text-slate-300">x={template.crmX} y={template.crmY}</span></div>
          <div>Fonte: <span className="text-slate-300">{template.fontFamily} {template.nameFontSize}px</span></div>
          <div className="flex items-center gap-1">
            Cor:
            <span className="inline-block w-3 h-3 rounded border border-slate-700" style={{ background: template.nameColor }} />
            <span className="text-slate-300">{template.nameColor}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateFormModal({ clients, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [imgUrl, setImgUrl] = useState('');         // blob URL para preview
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 }); // pixels naturais da imagem
  const previewRef = React.useRef(null);
  const draggingRef = React.useRef(null);  // 'name' | 'crm' | null
  const [form, setForm] = useState({
    apiClientId: clients[0]?.id || 0,
    name: 'Padrao',
    nameX: 80, nameY: 60, nameFontSize: 32, nameColor: '#FFFFFF',
    crmX: 80, crmY: 110, crmFontSize: 18, crmColor: '#E5E7EB',
    fontFamily: 'Arial',
    isDefault: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleFile = (f) => {
    setFile(f);
    if (imgUrl) URL.revokeObjectURL(imgUrl);
    setImgUrl(f ? URL.createObjectURL(f) : '');
  };

  // Calcula a escala atual entre pixels da imagem e pixels exibidos.
  // Tudo posicionado/clicado eh em pixels da imagem (que vai pro backend).
  const getScale = () => {
    const el = previewRef.current;
    if (!el || !imgDims.w) return 1;
    return el.clientWidth / imgDims.w;
  };

  const onPointerDown = (which) => (e) => {
    e.preventDefault();
    draggingRef.current = which;
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    const scale = getScale();
    const xPx = Math.round((e.clientX - rect.left) / scale);
    const yPx = Math.round((e.clientY - rect.top) / scale);
    const clampedX = Math.max(0, Math.min(imgDims.w, xPx));
    const clampedY = Math.max(0, Math.min(imgDims.h, yPx));
    if (draggingRef.current === 'name') {
      setForm(f => ({ ...f, nameX: clampedX, nameY: clampedY }));
    } else if (draggingRef.current === 'crm') {
      setForm(f => ({ ...f, crmX: clampedX, crmY: clampedY }));
    }
  };

  const onPointerUp = () => { draggingRef.current = null; };

  React.useEffect(() => {
    return () => { if (imgUrl) URL.revokeObjectURL(imgUrl); };
  }, [imgUrl]);

  const save = async () => {
    if (!file) { setError('Selecione uma imagem'); return; }
    if (!form.apiClientId) { setError('Selecione um cliente'); return; }
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      const res = await adminApi.createTemplate(fd);
      if (res?.error) setError(res.error); else onSaved();
    } catch (err) { setError('Erro ao subir template'); }
    finally { setSaving(false); }
  };

  const scale = getScale();

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-5xl max-h-[95vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}
           onMouseMove={onPointerMove}
           onMouseUp={onPointerUp}
           onMouseLeave={onPointerUp}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Novo Template de Fundo</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ----------- Coluna esquerda: meta + arquivo ----------- */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Cliente</label>
              <select
                value={form.apiClientId}
                onChange={e => update('apiClientId', Number(e.target.value))}
                className="w-full bg-slate-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={0}>-- selecione --</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>

            <Field label="Nome do template" value={form.name} onChange={v => update('name', v)} />

            <div>
              <label className="block text-xs text-slate-400 mb-1">Imagem (PNG/JPG, recomendado 1280x720)</label>
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={e => handleFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-300 file:mr-3 file:px-3 file:py-2 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700"
              />
              {file && <p className="text-xs text-slate-500 mt-1">{file.name} - {(file.size/1024).toFixed(0)} KB - {imgDims.w}x{imgDims.h}px</p>}
            </div>

            {/* Controles do NOME */}
            <div className="pt-3 border-t border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full" style={{ background: form.nameColor, border: '1px solid #475569' }} />
                <p className="text-xs uppercase text-slate-400 font-semibold">Nome do medico</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <NumField label="X" value={form.nameX} onChange={v => update('nameX', v)} />
                <NumField label="Y" value={form.nameY} onChange={v => update('nameY', v)} />
                <NumField label="Tamanho" value={form.nameFontSize} onChange={v => update('nameFontSize', v)} />
              </div>
              <ColorField label="Cor" value={form.nameColor} onChange={v => update('nameColor', v)} />
            </div>

            {/* Controles do CRM */}
            <div className="pt-3 border-t border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full" style={{ background: form.crmColor, border: '1px solid #475569' }} />
                <p className="text-xs uppercase text-slate-400 font-semibold">CRM</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <NumField label="X" value={form.crmX} onChange={v => update('crmX', v)} />
                <NumField label="Y" value={form.crmY} onChange={v => update('crmY', v)} />
                <NumField label="Tamanho" value={form.crmFontSize} onChange={v => update('crmFontSize', v)} />
              </div>
              <ColorField label="Cor" value={form.crmColor} onChange={v => update('crmColor', v)} />
            </div>

            <Field label="Fonte (Windows)" value={form.fontFamily} onChange={v => update('fontFamily', v)} />

            <label className="flex items-center gap-2 text-sm pt-2">
              <input type="checkbox" checked={form.isDefault} onChange={e => update('isDefault', e.target.checked)} />
              Template padrao do cliente
            </label>
          </div>

          {/* ----------- Coluna direita: preview interativo ----------- */}
          <div>
            <p className="text-xs text-slate-400 mb-2">
              Preview - <span className="text-slate-300">arraste o nome e o CRM</span> pra posicionar
            </p>
            {!imgUrl ? (
              <div className="aspect-video bg-slate-800 border border-dashed border-slate-700 rounded-lg flex items-center justify-center text-slate-500 text-sm">
                Selecione uma imagem para ver o preview
              </div>
            ) : (
              <div
                ref={previewRef}
                className="relative w-full aspect-video bg-slate-800 rounded-lg overflow-hidden select-none"
                style={{ aspectRatio: '16 / 9' }}
              >
                {/*
                  Forcamos aspecto 16:9 e object-cover porque o backend vai recortar
                  a imagem para 1280x720 ao salvar, e o LiveKit VirtualBackground
                  tambem usa cover. Assim, o que o admin posiciona aqui eh o que
                  vai aparecer no video final - sem surpresas de corte no topo.
                */}
                <img
                  src={imgUrl}
                  alt="template preview"
                  className="w-full h-full object-cover pointer-events-none"
                  onLoad={() => setImgDims({ w: 1280, h: 720 })}
                  draggable={false}
                />
                {/* Texto NOME draggable */}
                <DraggableLabel
                  label="Dr. Nome do Medico"
                  x={form.nameX} y={form.nameY}
                  fontSize={form.nameFontSize}
                  color={form.nameColor}
                  fontFamily={form.fontFamily}
                  scale={scale}
                  onPointerDown={onPointerDown('name')}
                />
                {/* Texto CRM draggable */}
                <DraggableLabel
                  label="CRM/UF 12345"
                  x={form.crmX} y={form.crmY}
                  fontSize={form.crmFontSize}
                  color={form.crmColor}
                  fontFamily={form.fontFamily}
                  scale={scale}
                  onPointerDown={onPointerDown('crm')}
                />
              </div>
            )}
            <p className="text-[10px] text-slate-500 mt-1.5">
              Coordenadas em pixels da imagem original. Texto eh ancorado no canto superior-esquerdo (igual ImageSharp).
            </p>
          </div>
        </div>

        {error && <p className="text-rose-400 text-sm mt-3">{error}</p>}

        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-500 rounded-lg py-2.5 font-medium disabled:opacity-50">
            {saving ? 'Salvando...' : 'Salvar Template'}
          </button>
          <button onClick={onClose} className="px-4 bg-slate-800 hover:bg-slate-700 rounded-lg">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function DraggableLabel({ label, x, y, fontSize, color, fontFamily, scale, onPointerDown }) {
  // Posicionamento absoluto traduzindo pixel-da-imagem para pixel-do-display.
  // text-shadow em em-units escala com o tamanho da fonte e imita visualmente
  // a sombra aplicada pelo ComposeAsync no servidor (sem ficar pesado demais
  // em fontes pequenas nem sumir em fontes grandes).
  return (
    <span
      onMouseDown={onPointerDown}
      className="absolute cursor-move whitespace-nowrap font-semibold pointer-events-auto"
      style={{
        left: `${x * scale}px`,
        top: `${y * scale}px`,
        fontSize: `${fontSize * scale}px`,
        color,
        fontFamily: `${fontFamily}, system-ui`,
        textShadow: '0 0.04em 0.12em rgba(0,0,0,0.6)',
        lineHeight: 1,
        userSelect: 'none',
      }}
      title={`${label} - arraste para reposicionar`}
    >
      {label}
    </span>
  );
}

function NumField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-[10px] text-slate-500 mb-0.5 uppercase">{label}</label>
      <input
        type="number"
        value={value}
        onChange={e => onChange(Number(e.target.value) || 0)}
        className="w-full bg-slate-800 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
      />
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div className="mt-2">
      <label className="block text-[10px] text-slate-500 mb-0.5 uppercase">{label}</label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={value}
          onChange={e => onChange(e.target.value.toUpperCase())}
          className="w-10 h-9 bg-slate-800 rounded cursor-pointer border border-slate-700"
        />
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="flex-1 bg-slate-800 rounded px-2 py-1.5 text-sm font-mono outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
    </div>
  );
}

// ====================================================================
// DOCTORS TAB
// ====================================================================

function DoctorsTab({ clients, templates }) {
  const [selectedClient, setSelectedClient] = useState(clients[0]?.id || null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const client = clients.find(c => c.id === selectedClient);

  useEffect(() => {
    if (!client?.apiKey) { setDoctors([]); return; }
    setLoading(true);
    doctorsApi.list(client.apiKey)
      .then(r => setDoctors(Array.isArray(r) ? r : []))
      .finally(() => setLoading(false));
  }, [client?.apiKey]);

  // Toggle otimista: aplica o novo estado na UI antes do PUT pra o clique
  // nao parecer travado, e reverte se o backend recusar. ExternalId e a
  // chave (alinhado com o resto do DoctorsController).
  const handleTogglePipAuto = async (doctor) => {
    if (!client?.apiKey) return;
    const next = !doctor.pipAutoEnabled;
    setTogglingId(doctor.id);
    setDoctors(ds => ds.map(d => d.id === doctor.id ? { ...d, pipAutoEnabled: next } : d));
    try {
      await doctorsApi.setPipAuto(client.apiKey, doctor.externalId, next);
    } catch (err) {
      console.error('Falha ao atualizar auto-PiP:', err);
      setDoctors(ds => ds.map(d => d.id === doctor.id ? { ...d, pipAutoEnabled: !next } : d));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm text-slate-400">Cliente:</label>
        <select
          value={selectedClient || ''}
          onChange={e => setSelectedClient(Number(e.target.value))}
          className="bg-slate-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {clients.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <h2 className="font-semibold">Medicos cadastrados ({doctors.length})</h2>
          <span className="text-xs text-slate-500">
            HelpGlobal sincroniza via POST /api/doctors/sync
          </span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500">Carregando...</div>
        ) : doctors.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Stethoscope size={48} className="mx-auto mb-3 opacity-30" />
            <p>Nenhum medico cadastrado para este cliente ainda.</p>
            <p className="text-xs mt-2">A HelpGlobal precisa enviar POST /api/doctors/sync com externalId, name, crmNumero, crmEstado.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-950/50 text-xs text-slate-400 uppercase">
              <tr>
                <th className="text-left p-3">Medico</th>
                <th className="text-left p-3">CRM</th>
                <th className="text-left p-3">External ID</th>
                <th className="text-left p-3">Template</th>
                <th className="text-left p-3">Background</th>
                <th className="text-center p-3">Auto PiP</th>
                <th className="text-right p-3">Atualizado</th>
              </tr>
            </thead>
            <tbody>
              {doctors.map(d => {
                const tpl = templates.find(t => t.id === d.templateId);
                const pipOn = !!d.pipAutoEnabled;
                const isToggling = togglingId === d.id;
                return (
                  <tr key={d.id} className="border-t border-slate-800 hover:bg-slate-800/40">
                    <td className="p-3 font-medium">{d.name}</td>
                    <td className="p-3 text-slate-300">
                      {d.crmNumero
                        ? (() => {
                            const prefix = (d.crmConselho || 'CRM').toUpperCase();
                            return d.crmEstado
                              ? `${prefix}/${d.crmEstado} ${d.crmNumero}`
                              : `${prefix} ${d.crmNumero}`;
                          })()
                        : '-'}
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-500">{d.externalId}</td>
                    <td className="p-3 text-xs">{tpl?.name || (d.templateId ? `#${d.templateId}` : 'default')}</td>
                    <td className="p-3">
                      {d.backgroundImageUrl ? (
                        <a href={d.backgroundImageUrl} target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline text-xs">
                          ver PNG
                        </a>
                      ) : (
                        <span className="text-slate-500 text-xs">sem template</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => handleTogglePipAuto(d)}
                        disabled={isToggling}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          pipOn
                            ? 'bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30'
                            : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                        } ${isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                        title={pipOn
                          ? 'Auto-PiP ligado pra este medico: a janela de PiP abre sozinha quando ele troca de aba. Clique pra desligar.'
                          : 'Auto-PiP desligado pra este medico. Clique pra ligar - a janela passara a abrir sozinha quando ele trocar de aba.'}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${pipOn ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                        {pipOn ? 'Ligado' : 'Desligado'}
                      </button>
                    </td>
                    <td className="p-3 text-right text-xs text-slate-500">
                      {d.updatedAt ? new Date(d.updatedAt).toLocaleString('pt-BR') : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Diagnostico: eventos de erro/warn/info do navegador (medico + paciente).
// Ajuda a responder "o que aconteceu na consulta X?" sem ter que pedir print
// pro medico. Veja DiagnosticsController + services/diagnostics.js.
// =========================================================================
function DiagnosticsTab() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState([]);
  const [lastHour, setLastHour] = useState([]);
  const [blockedDevices, setBlockedDevices] = useState(0);
  const [backgroundIssues, setBackgroundIssues] = useState(0);
  const [turnUsage, setTurnUsage] = useState(null);
  const [turnHours, setTurnHours] = useState(24);
  const [audioIssues, setAudioIssues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ severity: '', roomId: '', externalId: '', eventType: '', name: '', limit: 200 });
  const [expanded, setExpanded] = useState(null);

  // Lista canonica usada pra clickar nos cards e filtrar a lista de eventos
  // pelos mesmos tipos que o backend agrega. Mantem em sincronia com o
  // warnTypes/resolveTypes em DiagnosticsController.AudioIssues.
  const AUDIO_WARN_TYPES = 'audio_track_browser_muted,audio_auto_toggle_resubscribe,audio_play_rejected,audio_blocked_silently,audio_play_rejected_on_unmute,stuck_audio_recovered';
  const AUDIO_RESOLVE_TYPES = 'audio_unblocked_by_user,audio_track_browser_unmuted';

  const reload = async () => {
    setLoading(true);
    try {
      const [ev, rs, tu, ai] = await Promise.allSettled([
        adminApi.listDiagnostics(filters),
        adminApi.listDiagnosticsRooms(24),
        adminApi.getTurnUsage(turnHours),
        adminApi.getAudioIssues(1),
      ]);
      if (ev.status === 'fulfilled') {
        setEvents(ev.value?.events || []);
        setLastHour(ev.value?.lastHour || []);
        setBlockedDevices(ev.value?.blockedDevices || 0);
        setBackgroundIssues(ev.value?.backgroundIssues || 0);
      } else {
        setEvents([]); setLastHour([]); setBlockedDevices(0); setBackgroundIssues(0);
        console.error('diagnostics load', ev.reason);
      }
      setSummary(rs.status === 'fulfilled' ? (rs.value || []) : []);
      setTurnUsage(tu.status === 'fulfilled' ? (tu.value || null) : null);
      setAudioIssues(ai.status === 'fulfilled' ? (ai.value || null) : null);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const errorCount = lastHour.find(x => x.severity === 'error')?.count || 0;
  const warnCount  = lastHour.find(x => x.severity === 'warn')?.count || 0;
  const infoCount  = lastHour.find(x => x.severity === 'info')?.count || 0;

  return (
    <div className="space-y-6">
      {/* Resumo ultima hora. Cards clicaveis aplicam filtros tematicos pra o
          admin ir direto ao ponto sem ter que lembrar o nome do event type.
          "Falhas fundo" agrega background_apply_failed + background_missing
          via lista por virgula no eventType (backend suporta isso). */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Erros (1h)" value={errorCount} icon={AlertTriangle} color="red" />
        <StatCard label="Warnings (1h)" value={warnCount} icon={AlertTriangle} color="amber" />
        <StatCard
          label="Bloqueios camera/mic (1h)"
          value={blockedDevices}
          icon={MicOff}
          color="rose"
          onClick={() => {
            setFilters(f => ({ ...f, eventType: 'device_check_failed', severity: '' }));
            setTimeout(reload, 0);
          }}
        />
        <StatCard
          label="Falhas fundo (1h)"
          value={backgroundIssues}
          icon={ImageIcon}
          color="purple"
          onClick={() => {
            setFilters(f => ({ ...f, eventType: 'background_apply_failed,background_missing', severity: '' }));
            setTimeout(reload, 0);
          }}
        />
        {/* Audio com problema = SALAS distintas com 1+ warn de audio na ultima
            hora. Conta sala (nao evento) - 1 sala com 50 warns nao infla pra 50.
            Click filtra a lista de eventos pelos mesmos warn types pra inspecao
            individual. */}
        <StatCard
          label="Audio c/ problema (1h)"
          value={audioIssues?.total || 0}
          icon={Volume2}
          color="amber"
          onClick={() => {
            setFilters(f => ({ ...f, eventType: AUDIO_WARN_TYPES, severity: '' }));
            setTimeout(reload, 0);
          }}
        />
        {/* Audio resolvido = subset das salas com problema que tiveram sinal
            de recuperacao (overlay clicado, mst.unmuted apos warn, ou >60s de
            atividade saudavel apos ultimo warn). Heuristica conservadora -
            pode subestimar. Click mostra so os eventos de resolucao. */}
        <StatCard
          label="Audio resolvido (1h)"
          value={audioIssues?.resolved || 0}
          icon={CheckCircle2}
          color="emerald"
          onClick={() => {
            setFilters(f => ({ ...f, eventType: AUDIO_RESOLVE_TYPES, severity: '' }));
            setTimeout(reload, 0);
          }}
        />
        <StatCard label="Info (1h)" value={infoCount} icon={Activity} color="sky" />
        <StatCard label="Salas c/ evento (24h)" value={summary.length} icon={Video} color="indigo" />
      </div>

      {/* Uso de TURN: mostra quantas chamadas usaram relay (TURN) vs direto
          P2P/STUN, e qual provider serviu cada relay. Util pra medir o
          impacto real da integracao Cloudflare + coturn proprio em producao -
          se viaTurn% subiu sem aumentar erros, e sinal de que pacientes em
          rede ruim agora estao conseguindo conectar (antes caiam em
          "could not establish pc connection"). */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-semibold flex items-center gap-2">
            <Activity size={16} className="text-indigo-400" />
            Uso de TURN (relay) — ultimas {turnHours}h
          </div>
          <div className="flex items-center gap-2 text-xs">
            {[6, 24, 72, 168].map(h => (
              <button
                key={h}
                onClick={() => { setTurnHours(h); setTimeout(reload, 0); }}
                className={`px-2 py-1 rounded ${turnHours === h ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
              >{h}h</button>
            ))}
          </div>
        </div>
        <div className="p-4">
          {!turnUsage || turnUsage.totalSamples === 0 ? (
            <div className="text-sm text-slate-400">
              Sem amostras de ICE no periodo. Eventos `ice_pair` aparecem ~5s apos cada conexao bem-sucedida.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Barra horizontal direct vs via TURN */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-300">{turnUsage.totalSamples} amostras totais</span>
                  <span className="text-slate-500">
                    direto {turnUsage.directPct}% · TURN {turnUsage.turnPct}%
                  </span>
                </div>
                <div className="flex h-3 rounded overflow-hidden bg-slate-800">
                  <div
                    className="bg-emerald-500"
                    style={{ width: `${turnUsage.directPct}%` }}
                    title={`Direto (host/srflx): ${turnUsage.direct} (${turnUsage.directPct}%)`}
                  />
                  <div
                    className="bg-amber-500"
                    style={{ width: `${turnUsage.turnPct}%` }}
                    title={`Via TURN (relay): ${turnUsage.viaTurn} (${turnUsage.turnPct}%)`}
                  />
                </div>
              </div>

              {/* Breakdown por protocolo (UDP vs TCP). Em rede saudavel,
                  esperado e UDP dominar - quando TCP comeca a subir e
                  sinal de que mais pacientes estao em rede com UDP
                  bloqueado/lento e o auto-retry esta caindo pra TCP. */}
              {turnUsage.byProtocol && (turnUsage.byProtocol.udp + turnUsage.byProtocol.tcp) > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-300">Protocolo de transporte</span>
                    <span className="text-slate-500">
                      UDP {turnUsage.byProtocol.udpPct}% · TCP {turnUsage.byProtocol.tcpPct}%
                    </span>
                  </div>
                  <div className="flex h-3 rounded overflow-hidden bg-slate-800">
                    <div
                      className="bg-cyan-500"
                      style={{ width: `${turnUsage.byProtocol.udpPct}%` }}
                      title={`UDP: ${turnUsage.byProtocol.udp} (${turnUsage.byProtocol.udpPct}%) - melhor qualidade`}
                    />
                    <div
                      className="bg-fuchsia-500"
                      style={{ width: `${turnUsage.byProtocol.tcpPct}%` }}
                      title={`TCP: ${turnUsage.byProtocol.tcp} (${turnUsage.byProtocol.tcpPct}%) - latencia maior, indicador de UDP bloqueado`}
                    />
                  </div>
                  {(turnUsage.byProtocol.directUdp + turnUsage.byProtocol.directTcp + turnUsage.byProtocol.relayUdp + turnUsage.byProtocol.relayTcp) > 0 && (
                    <div className="mt-2 grid grid-cols-2 gap-3 text-xs text-slate-400">
                      <div>
                        Direto: <span className="text-cyan-300">{turnUsage.byProtocol.directUdp} UDP</span> · <span className="text-fuchsia-300">{turnUsage.byProtocol.directTcp} TCP</span>
                      </div>
                      <div>
                        Relay: <span className="text-cyan-300">{turnUsage.byProtocol.relayUdp} UDP</span> · <span className="text-fuchsia-300">{turnUsage.byProtocol.relayTcp} TCP</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Estatistica do auto-retry silencioso. Mede a saude geral
                  das primeiras tentativas de connect: quantas precisaram
                  de retry e quantas conseguiram resolver via retry vs
                  esgotaram tentativas. retryFrequencyPct alto sugere que
                  vale baixar peerConnectionTimeout ou forcar TCP em iOS;
                  retrySuccessRatePct alto confirma que o retry e cura
                  efetiva. */}
              {turnUsage.retries && turnUsage.retries.sessionsConnected > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-300">Auto-retry de conexao</span>
                    <span className="text-slate-500">
                      {turnUsage.retries.retryFrequencyPct}% das conexoes precisaram de retry
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="bg-slate-800/60 rounded p-2">
                      <div className="text-slate-400">Conexoes ok</div>
                      <div className="text-emerald-300 font-semibold text-base">{turnUsage.retries.sessionsConnected}</div>
                    </div>
                    <div className="bg-slate-800/60 rounded p-2">
                      <div className="text-slate-400">Precisaram retry</div>
                      <div className="text-amber-300 font-semibold text-base">{turnUsage.retries.retrySuccessfulCount}</div>
                    </div>
                    <div className="bg-slate-800/60 rounded p-2">
                      <div className="text-slate-400">Retries que falharam</div>
                      <div className="text-rose-300 font-semibold text-base">{turnUsage.retries.retriesGivenUp}</div>
                    </div>
                    <div className="bg-slate-800/60 rounded p-2">
                      <div className="text-slate-400">Sucesso do retry</div>
                      <div className="text-indigo-300 font-semibold text-base">{turnUsage.retries.retrySuccessRatePct}%</div>
                    </div>
                  </div>

                  {/* Breakdown de retries por plataforma e browser. Confirma
                      "iOS afeta mais?" sem precisar abrir cada evento. */}
                  {(turnUsage.retries.byPlatform?.length > 0 || turnUsage.retries.byBrowser?.length > 0) && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {turnUsage.retries.byPlatform?.length > 0 && (
                        <div>
                          <div className="text-xs text-slate-400 mb-1">Retries por plataforma</div>
                          <table className="w-full text-xs">
                            <tbody>
                              {turnUsage.retries.byPlatform.map((p, i) => (
                                <tr key={i} className="border-t border-slate-800">
                                  <td className="py-1 text-slate-300">{p.platform}</td>
                                  <td className="py-1 text-right text-slate-400">{p.count}</td>
                                  <td className="py-1 text-right text-slate-500 w-12">{p.pct}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {turnUsage.retries.byBrowser?.length > 0 && (
                        <div>
                          <div className="text-xs text-slate-400 mb-1">Retries por browser</div>
                          <table className="w-full text-xs">
                            <tbody>
                              {turnUsage.retries.byBrowser.map((b, i) => (
                                <tr key={i} className="border-t border-slate-800">
                                  <td className="py-1 text-slate-300">{b.browser}</td>
                                  <td className="py-1 text-right text-slate-400">{b.count}</td>
                                  <td className="py-1 text-right text-slate-500 w-12">{b.pct}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Tempo medio/p50/p95 entre /join e session_connected.
                  p95 e o "pior caso comum" - se passa de ~10s, paciente
                  costuma achar que travou. Acompanhar essa metrica
                  conforme ajusta peerConnectionTimeout. */}
              {turnUsage.connectTime && turnUsage.connectTime.samples > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-slate-300">Tempo ate conectar</span>
                    <span className="text-slate-500">
                      {turnUsage.connectTime.samples} amostras
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-slate-800/60 rounded p-2">
                      <div className="text-slate-400">Media</div>
                      <div className="text-sky-300 font-semibold text-base">
                        {(turnUsage.connectTime.avgMs / 1000).toFixed(1)}s
                      </div>
                    </div>
                    <div className="bg-slate-800/60 rounded p-2">
                      <div className="text-slate-400">p50 (mediana)</div>
                      <div className="text-emerald-300 font-semibold text-base">
                        {(turnUsage.connectTime.p50Ms / 1000).toFixed(1)}s
                      </div>
                    </div>
                    <div className="bg-slate-800/60 rounded p-2">
                      <div className="text-slate-400">p95 (pior caso)</div>
                      <div className={`font-semibold text-base ${
                        turnUsage.connectTime.p95Ms > 10000 ? 'text-rose-300' :
                        turnUsage.connectTime.p95Ms > 6000 ? 'text-amber-300' :
                        'text-emerald-300'
                      }`}>
                        {(turnUsage.connectTime.p95Ms / 1000).toFixed(1)}s
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Drops mid-call: sessoes que conectaram e depois cairam
                  com isFailure (drop tecnico, nao saida intencional).
                  Distinto de connect failure - aqui o problema e durante
                  a chamada. Esperado <5%; acima disso pode ser problema
                  de rede do paciente, perda de pacotes no servidor, ou
                  ICE restart falhando. */}
              {turnUsage.midCallDrops && turnUsage.midCallDrops.sessionsConnected > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-slate-300">Drops mid-call</span>
                    <span className="text-slate-500">
                      {turnUsage.midCallDrops.count} de {turnUsage.midCallDrops.sessionsConnected} sessoes
                    </span>
                  </div>
                  <div className="bg-slate-800/60 rounded p-3 flex items-center justify-between">
                    <div className="text-xs text-slate-400">
                      % de chamadas que cairam mid-call (apos session_connected)
                    </div>
                    <div className={`font-semibold text-lg ${
                      turnUsage.midCallDrops.pct > 10 ? 'text-rose-300' :
                      turnUsage.midCallDrops.pct > 5  ? 'text-amber-300' :
                      'text-emerald-300'
                    }`}>
                      {turnUsage.midCallDrops.pct}%
                    </div>
                  </div>
                </div>
              )}

              {/* Breakdown por provider quando ha relays */}
              {turnUsage.viaTurn > 0 && (
                <div>
                  <div className="text-xs text-slate-400 mb-2">Quem servi os relays:</div>
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500 uppercase">
                      <tr>
                        <th className="text-left py-1">Provider</th>
                        <th className="text-left py-1">Endereco</th>
                        <th className="text-right py-1">Amostras</th>
                        <th className="text-right py-1">% do total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {turnUsage.byProvider.map((p, i) => (
                        <tr key={i} className="border-t border-slate-800">
                          <td className="py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              p.provider === 'Cloudflare' ? 'bg-orange-500/20 text-orange-300' :
                              p.provider.includes('Coturn') ? 'bg-blue-500/20 text-blue-300' :
                              'bg-slate-700 text-slate-300'
                            }`}>{p.provider}</span>
                          </td>
                          <td className="py-2 font-mono text-xs text-slate-400">{p.address}</td>
                          <td className="py-2 text-right">{p.count}</td>
                          <td className="py-2 text-right text-slate-300">{p.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Salas com mais problemas */}
      {summary.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-800 font-semibold">Salas com eventos (ultimas 24h)</div>
          <div className="max-h-64 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/50 text-xs text-slate-400 uppercase">
                <tr>
                  <th className="text-left p-2">RoomId</th>
                  <th className="text-right p-2">Erros</th>
                  <th className="text-right p-2">Warns</th>
                  <th className="text-right p-2">Total</th>
                  <th className="text-right p-2">Ultimo</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {summary.slice(0, 30).map(s => (
                  <tr key={s.roomId} className="border-t border-slate-800 hover:bg-slate-800/30">
                    <td className="p-2 font-mono text-xs">{s.roomId}</td>
                    <td className="p-2 text-right text-red-400 font-semibold">{s.errors}</td>
                    <td className="p-2 text-right text-amber-400">{s.warns}</td>
                    <td className="p-2 text-right">{s.total}</td>
                    <td className="p-2 text-right text-xs text-slate-500">
                      {new Date(s.lastAt).toLocaleString('pt-BR')}
                    </td>
                    <td className="p-2 text-right">
                      <button
                        className="text-indigo-400 hover:underline text-xs"
                        onClick={() => setFilters(f => ({ ...f, roomId: s.roomId }))}
                      >filtrar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          <select
            className="bg-slate-950 border border-slate-800 rounded px-2 py-2 text-sm"
            value={filters.severity}
            onChange={e => setFilters(f => ({ ...f, severity: e.target.value }))}
          >
            <option value="">Severity (todas)</option>
            <option value="error">error</option>
            <option value="warn">warn</option>
            <option value="info">info</option>
          </select>
          <input
            className="bg-slate-950 border border-slate-800 rounded px-2 py-2 text-sm"
            placeholder="Nome (medico ou paciente)"
            value={filters.name}
            onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
          />
          <input
            className="bg-slate-950 border border-slate-800 rounded px-2 py-2 text-sm"
            placeholder="RoomId"
            value={filters.roomId}
            onChange={e => setFilters(f => ({ ...f, roomId: e.target.value }))}
          />
          <input
            className="bg-slate-950 border border-slate-800 rounded px-2 py-2 text-sm"
            placeholder="Medico (externalId)"
            value={filters.externalId}
            onChange={e => setFilters(f => ({ ...f, externalId: e.target.value }))}
          />
          <input
            className="bg-slate-950 border border-slate-800 rounded px-2 py-2 text-sm"
            placeholder="EventType (ex: processor_failed)"
            value={filters.eventType}
            onChange={e => setFilters(f => ({ ...f, eventType: e.target.value }))}
          />
          <input
            type="number"
            className="bg-slate-950 border border-slate-800 rounded px-2 py-2 text-sm"
            placeholder="Limit"
            value={filters.limit}
            onChange={e => setFilters(f => ({ ...f, limit: Number(e.target.value) || 200 }))}
          />
          <div className="flex gap-2">
            <button
              onClick={reload}
              className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 rounded px-3 py-2 text-sm"
            >
              <RefreshCw size={14} /> Buscar
            </button>
            <button
              onClick={() => { setFilters({ severity: '', roomId: '', externalId: '', eventType: '', name: '', limit: 200 }); setTimeout(reload, 0); }}
              className="px-3 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Eventos */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <span className="font-semibold">Eventos ({events.length})</span>
          <span className="text-xs text-slate-500">clique numa linha pra ver UA + metadata</span>
        </div>
        {loading ? (
          <div className="p-12 text-center text-slate-500">Carregando...</div>
        ) : events.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <AlertTriangle size={48} className="mx-auto mb-3 opacity-30" />
            <p>Nenhum evento encontrado com esses filtros.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/50 text-xs text-slate-400 uppercase">
                <tr>
                  <th className="text-left p-2 whitespace-nowrap">Quando</th>
                  <th className="text-left p-2">Sev</th>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-left p-2">Sala</th>
                  <th className="text-left p-2">Papel</th>
                  <th className="text-left p-2">Nome</th>
                  <th className="text-left p-2">Plataforma</th>
                  <th className="text-left p-2">Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => (
                  <React.Fragment key={ev.id}>
                    <tr
                      className={`border-t border-slate-800 cursor-pointer hover:bg-slate-800/30 ${
                        ev.severity === 'error' ? 'bg-red-950/20' : ev.severity === 'warn' ? 'bg-amber-950/10' : ''
                      }`}
                      onClick={() => setExpanded(expanded === ev.id ? null : ev.id)}
                    >
                      <td className="p-2 whitespace-nowrap text-xs text-slate-400">
                        {new Date(ev.createdAt).toLocaleString('pt-BR')}
                      </td>
                      <td className="p-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                          ev.severity === 'error' ? 'bg-red-600/20 text-red-300'
                          : ev.severity === 'warn' ? 'bg-amber-600/20 text-amber-300'
                          : 'bg-slate-700/40 text-slate-300'
                        }`}>{ev.severity}</span>
                      </td>
                      <td className="p-2 font-mono text-xs">{ev.eventType}</td>
                      <td className="p-2 font-mono text-xs text-slate-400">{ev.roomId || '-'}</td>
                      <td className="p-2 text-xs">{ev.participantRole || '-'}</td>
                      <td className="p-2 text-xs">{ev.displayName || ev.externalId || '-'}</td>
                      <td className="p-2 text-xs">{ev.platform || '-'}/{ev.browser || '-'}</td>
                      <td className="p-2 text-xs max-w-md truncate">{ev.message}</td>
                    </tr>
                    {expanded === ev.id && (
                      <tr className="bg-slate-950/60">
                        <td colSpan={8} className="p-3">
                          <div className="space-y-2 text-xs">
                            {ev.userAgent && (
                              <div><span className="text-slate-500">UA:</span> <span className="font-mono break-all">{ev.userAgent}</span></div>
                            )}
                            {ev.participantIdentity && (
                              <div><span className="text-slate-500">Identity:</span> <span className="font-mono">{ev.participantIdentity}</span></div>
                            )}
                            {ev.externalId && (
                              <div><span className="text-slate-500">Medico externalId:</span> <span className="font-mono">{ev.externalId}</span></div>
                            )}
                            {ev.clientIp && (
                              <div><span className="text-slate-500">IP:</span> <span className="font-mono">{ev.clientIp}</span></div>
                            )}
                            {ev.metadata && (
                              <div>
                                <div className="text-slate-500 mb-1">Metadata:</div>
                                <pre className="bg-slate-950 border border-slate-800 rounded p-2 overflow-auto max-h-64 text-slate-200">
                                  {formatMetadata(ev.metadata)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function formatMetadata(raw) {
  if (!raw) return '';
  try { return JSON.stringify(JSON.parse(raw), null, 2); }
  catch { return raw; }
}
